import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Typography from '@mui/material/Typography';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useId, useState } from 'react';

import { ApiError } from '@/api/envelope';
import { changePortalUserRole, type OrganizationRole } from '@/api/generated';
import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/Dialog';
import { useReadOnly } from '@/auth/useViewingAs';
import { useToast } from '@/components/ui/useToast';
import { ROLE_LABELS, type PortalUserRow } from '@/lib/portalUsers';
import { testIdAttr, testIdPart } from '@/lib/testIds';
// PortalUserRow is also the shape cached under usersKey, which onMutate edits.

import { usersKey } from './keys';
import { ROLE_CHANGE_EFFECTS, ROLE_ORDER } from './roles';

interface ChangeRoleDialogProps {
  /** Who is being changed. Kept while the dialog fades out, so it does not go blank. */
  person: PortalUserRow | null;
  open: boolean;
  onClose: () => void;
}

/** "a Super Admin", "an Admin". */
function withArticle(role: OrganizationRole) {
  return `${role === 'ADMIN' ? 'an' : 'a'} ${ROLE_LABELS[role]}`;
}

/**
 * Make somebody a Super Admin, or an Admin.
 *
 * Confirm is the request: the dialog already says what the person gains or
 * loses, so asking a second time would only teach people to click through.
 *
 * A refusal closes the dialog rather than keeping it open. It only ever means
 * the list was out of date - the last Super Admin is gone, the person has
 * left - so there is nothing in the dialog to correct; the page's list is
 * what was wrong, and it is fetched again.
 */
export function ChangeRoleDialog({
  person,
  open: asked,
  onClose,
}: ChangeRoleDialogProps) {
  // Never opens while staff view the organization, whatever asked it to.
  const readOnly = useReadOnly();
  const open = asked && !readOnly;
  const queryClient = useQueryClient();
  const toast = useToast();
  const id = useId();
  const labelId = `${id}-roles`;

  const [choice, setChoice] = useState<OrganizationRole | null>(person?.role ?? null);

  /*
   * Start from the person's current role every time it opens. Adjusted while
   * rendering, as the status dialog does, so the previous choice is never
   * painted first.
   */
  const [shownFor, setShownFor] = useState<string | null>(null);
  const openedFor = open && person ? person.userId : null;
  if (openedFor !== shownFor) {
    setShownFor(openedFor);
    if (openedFor) setChoice(person!.role);
  }

  const change = useMutation({
    mutationFn: async ({ userId, role }: { userId: string; role: OrganizationRole }) => {
      await changePortalUserRole({
        path: { user_id: userId },
        body: { role },
        throwOnError: true,
      });
    },
    /*
     * Optimistic: the outcome is one row with a known new role, so the row
     * shows it the moment Confirm is pressed. The list as it was is kept, so
     * a refusal - rare, and only ever meaning the list was out of date - puts
     * the old role back while the refetch fetches the truth.
     */
    onMutate: async ({ userId, role }) => {
      // Stop any fetch already in flight from overwriting the predicted row.
      await queryClient.cancelQueries({ queryKey: usersKey });
      const before = queryClient.getQueryData<PortalUserRow[]>(usersKey);
      queryClient.setQueryData<PortalUserRow[]>(usersKey, (rows) =>
        rows?.map((row) => (row.userId === userId ? { ...row, role } : row)),
      );
      onClose();
      return { before };
    },
    onSuccess: (_data, { role }) => {
      toast.show({ message: `${person?.name} is now ${withArticle(role)}.` });
    },
    onError: (error, _variables, context) => {
      if (context?.before) queryClient.setQueryData(usersKey, context.before);
      toast.show({
        message:
          (error instanceof ApiError ? error.serverMessage : null) ??
          'The role could not be changed. Please try again.',
        severity: 'error',
      });
    },
    // Fetched again either way: on success to confirm, on a refusal because
    // the refusal means what was on screen was wrong.
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: usersKey });
    },
  });

  const changed = person !== null && choice !== null && choice !== person.role;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={person ? `Change role: ${person.name}` : 'Change role'}
      description={
        person
          ? `${person.name} is currently ${withArticle(person.role)} of this organization.`
          : undefined
      }
      maxWidth="xs"
      testId="change-role-dialog"
      actions={
        <>
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={change.isPending}
            data-testid="change-role-dialog-cancel-button"
          >
            Cancel
          </Button>
          <Button
            disabled={!changed}
            loading={change.isPending}
            data-testid="change-role-dialog-confirm-button"
            onClick={() =>
              changed && change.mutate({ userId: person.userId, role: choice })
            }
          >
            Change role
          </Button>
        </>
      }
    >
      <FormControl sx={{ mb: 2 }}>
        <FormLabel id={labelId}>New role</FormLabel>
        <RadioGroup
          aria-labelledby={labelId}
          data-testid="change-role-dialog-role-group"
          value={choice ?? ''}
          onChange={(event) => setChoice(event.target.value as OrganizationRole)}
        >
          {ROLE_ORDER.map((role) => (
            <FormControlLabel
              key={role}
              value={role}
              control={
                <Radio
                  slotProps={{
                    input: testIdAttr(`change-role-dialog-${testIdPart(role)}-radio`),
                  }}
                />
              }
              label={
                role === person?.role
                  ? `${ROLE_LABELS[role]} (current)`
                  : ROLE_LABELS[role]
              }
            />
          ))}
        </RadioGroup>
      </FormControl>

      {/* Announced as it changes, so a screen reader user hears what the
          choice does as they move between the two. */}
      <Box
        role="status"
        aria-live="polite"
        sx={{ p: 2, borderRadius: 1, bgcolor: 'action.hover', minHeight: 56 }}
      >
        <Typography variant="body2" data-testid="change-role-dialog-effect-message">
          {changed
            ? ROLE_CHANGE_EFFECTS[choice]
            : 'Choose a different role to change it.'}
        </Typography>
      </Box>
    </Dialog>
  );
}
